/**
 * 2D geometry helpers shared by the world generator (Node), the mesh builder (browser)
 * and the tests. Everything works on flat `[x0,z0, x1,z1, ...]` arrays so that results
 * are trivially comparable and serialise without any object churn.
 */

export type Poly = number[];

const EPS = 1e-9;

// ---------------------------------------------------------------------------
// Basics
// ---------------------------------------------------------------------------

export function polyArea(poly: Poly): number {
  let a = 0;
  const n = poly.length / 2;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    a += poly[j * 2] * poly[i * 2 + 1] - poly[i * 2] * poly[j * 2 + 1];
  }
  return a / 2;
}

/** Rewinds a ring so its signed area is positive (counter-clockwise in x/z). */
export function ensureWinding(poly: Poly, wantPositive: boolean): Poly {
  const positive = polyArea(poly) > 0;
  if (positive === wantPositive) return poly;
  const out: Poly = new Array(poly.length);
  const n = poly.length / 2;
  for (let i = 0; i < n; i++) {
    out[i * 2] = poly[(n - 1 - i) * 2];
    out[i * 2 + 1] = poly[(n - 1 - i) * 2 + 1];
  }
  return out;
}

/** Drops a duplicated closing vertex and any consecutive near-duplicates. */
export function cleanRing(poly: Poly, tolerance = 1e-4): Poly {
  const out: Poly = [];
  const n = poly.length / 2;
  for (let i = 0; i < n; i++) {
    const x = poly[i * 2];
    const z = poly[i * 2 + 1];
    const m = out.length / 2;
    if (m > 0) {
      const px = out[(m - 1) * 2];
      const pz = out[(m - 1) * 2 + 1];
      if (Math.abs(px - x) < tolerance && Math.abs(pz - z) < tolerance) continue;
    }
    out.push(x, z);
  }
  // Close the loop by removing a final point coincident with the first.
  const m = out.length / 2;
  if (m > 1) {
    const dx = out[0] - out[(m - 1) * 2];
    const dz = out[1] - out[(m - 1) * 2 + 1];
    if (Math.abs(dx) < tolerance && Math.abs(dz) < tolerance) out.length -= 2;
  }
  return out;
}

export function polyCentroid(poly: Poly): { x: number; z: number } {
  const n = poly.length / 2;
  if (n === 0) return { x: 0, z: 0 };
  let cx = 0;
  let cz = 0;
  let a = 0;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const cross = poly[j * 2] * poly[i * 2 + 1] - poly[i * 2] * poly[j * 2 + 1];
    a += cross;
    cx += (poly[j * 2] + poly[i * 2]) * cross;
    cz += (poly[j * 2 + 1] + poly[i * 2 + 1]) * cross;
  }
  if (Math.abs(a) < EPS) {
    // Degenerate (collinear) ring — fall back to the vertex average.
    for (let i = 0; i < n; i++) {
      cx += poly[i * 2];
      cz += poly[i * 2 + 1];
    }
    return { x: cx / n, z: cz / n };
  }
  return { x: cx / (3 * a), z: cz / (3 * a) };
}

export function pointInPoly(poly: Poly, x: number, z: number): boolean {
  let inside = false;
  const n = poly.length / 2;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[i * 2];
    const zi = poly[i * 2 + 1];
    const xj = poly[j * 2];
    const zj = poly[j * 2 + 1];
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}

export function bboxOfPoly(poly: Poly): { minX: number; minZ: number; maxX: number; maxZ: number } {
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < poly.length; i += 2) {
    if (poly[i] < minX) minX = poly[i];
    if (poly[i] > maxX) maxX = poly[i];
    if (poly[i + 1] < minZ) minZ = poly[i + 1];
    if (poly[i + 1] > maxZ) maxZ = poly[i + 1];
  }
  return { minX, minZ, maxX, maxZ };
}

// ---------------------------------------------------------------------------
// Polylines
// ---------------------------------------------------------------------------

export function polylineLength(pts: Poly): number {
  let d = 0;
  for (let i = 2; i < pts.length; i += 2) {
    d += Math.hypot(pts[i] - pts[i - 2], pts[i + 1] - pts[i - 1]);
  }
  return d;
}

/** Squared distance from a point to a segment, plus the parametric foot t in [0,1]. */
export function distToSegment2(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): { d2: number; t: number; x: number; z: number } {
  const vx = bx - ax;
  const vz = bz - az;
  const len2 = vx * vx + vz * vz;
  let t = len2 < EPS ? 0 : ((px - ax) * vx + (pz - az) * vz) / len2;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const x = ax + vx * t;
  const z = az + vz * t;
  const dx = px - x;
  const dz = pz - z;
  return { d2: dx * dx + dz * dz, t, x, z };
}

/** Ramer–Douglas–Peucker. Keeps junctions crisp while shedding OSM's dense curve nodes. */
export function simplify(pts: Poly, tolerance: number): Poly {
  const n = pts.length / 2;
  if (n < 3) return pts.slice();
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const stack: [number, number][] = [[0, n - 1]];
  const tol2 = tolerance * tolerance;
  while (stack.length) {
    const [lo, hi] = stack.pop()!;
    let best = -1;
    let bestD = tol2;
    for (let i = lo + 1; i < hi; i++) {
      const { d2 } = distToSegment2(
        pts[i * 2],
        pts[i * 2 + 1],
        pts[lo * 2],
        pts[lo * 2 + 1],
        pts[hi * 2],
        pts[hi * 2 + 1],
      );
      if (d2 > bestD) {
        bestD = d2;
        best = i;
      }
    }
    if (best >= 0) {
      keep[best] = 1;
      stack.push([lo, best], [best, hi]);
    }
  }
  const out: Poly = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(pts[i * 2], pts[i * 2 + 1]);
  return out;
}

/** Evenly spaced samples along a polyline, always including both endpoints. */
export function resample(pts: Poly, spacing: number): Poly {
  const n = pts.length / 2;
  if (n < 2) return pts.slice();
  const out: Poly = [pts[0], pts[1]];
  let carry = 0;
  for (let i = 1; i < n; i++) {
    const ax = pts[(i - 1) * 2];
    const az = pts[(i - 1) * 2 + 1];
    const bx = pts[i * 2];
    const bz = pts[i * 2 + 1];
    const segLen = Math.hypot(bx - ax, bz - az);
    if (segLen < EPS) continue;
    let d = spacing - carry;
    while (d < segLen) {
      const t = d / segLen;
      out.push(ax + (bx - ax) * t, az + (bz - az) * t);
      d += spacing;
    }
    carry = segLen - (d - spacing);
  }
  const m = out.length / 2;
  const lastX = pts[(n - 1) * 2];
  const lastZ = pts[(n - 1) * 2 + 1];
  if (Math.hypot(out[(m - 1) * 2] - lastX, out[(m - 1) * 2 + 1] - lastZ) > spacing * 0.25) {
    out.push(lastX, lastZ);
  } else if (m > 1) {
    out[(m - 1) * 2] = lastX;
    out[(m - 1) * 2 + 1] = lastZ;
  }
  return out;
}

/** Point at a given arc-length along a polyline, with the local unit direction. */
export function pointAlong(
  pts: Poly,
  distance: number,
): { x: number; z: number; dx: number; dz: number } {
  const n = pts.length / 2;
  if (n === 0) return { x: 0, z: 0, dx: 0, dz: -1 };
  if (n === 1) return { x: pts[0], z: pts[1], dx: 0, dz: -1 };
  let remaining = Math.max(0, distance);
  for (let i = 1; i < n; i++) {
    const ax = pts[(i - 1) * 2];
    const az = pts[(i - 1) * 2 + 1];
    const bx = pts[i * 2];
    const bz = pts[i * 2 + 1];
    const segLen = Math.hypot(bx - ax, bz - az);
    if (segLen < EPS) continue;
    if (remaining <= segLen) {
      const t = remaining / segLen;
      return {
        x: ax + (bx - ax) * t,
        z: az + (bz - az) * t,
        dx: (bx - ax) / segLen,
        dz: (bz - az) / segLen,
      };
    }
    remaining -= segLen;
  }
  const ax = pts[(n - 2) * 2];
  const az = pts[(n - 2) * 2 + 1];
  const bx = pts[(n - 1) * 2];
  const bz = pts[(n - 1) * 2 + 1];
  const segLen = Math.max(EPS, Math.hypot(bx - ax, bz - az));
  return { x: bx, z: bz, dx: (bx - ax) / segLen, dz: (bz - az) / segLen };
}

/**
 * Arc-length of the closest point on a polyline to (x,z). Used to turn a car's position
 * into "how far along the route are you", which drives the live position table.
 */
export function projectOntoPolyline(pts: Poly, x: number, z: number): { distance: number; d2: number } {
  const n = pts.length / 2;
  let acc = 0;
  let best = { distance: 0, d2: Infinity };
  for (let i = 1; i < n; i++) {
    const ax = pts[(i - 1) * 2];
    const az = pts[(i - 1) * 2 + 1];
    const bx = pts[i * 2];
    const bz = pts[i * 2 + 1];
    const segLen = Math.hypot(bx - ax, bz - az);
    const hit = distToSegment2(x, z, ax, az, bx, bz);
    if (hit.d2 < best.d2) best = { distance: acc + hit.t * segLen, d2: hit.d2 };
    acc += segLen;
  }
  return best;
}

// ---------------------------------------------------------------------------
// Road ribbons
// ---------------------------------------------------------------------------

export interface RibbonMesh {
  /** x,y,z triples */
  positions: number[];
  /** u,v pairs — u across the road (0..1), v along it in metres/width */
  uvs: number[];
  indices: number[];
}

const MITER_LIMIT = 2.5;

/**
 * Turns a centreline into a flat quad strip of the given width.
 *
 * Joins are mitered, with the miter length clamped so that a hairpin in the OSM geometry
 * produces a slightly blunt corner rather than a spike shooting off across the city.
 */
export function ribbon(pts: Poly, width: number, y = 0): RibbonMesh {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const line = dedupeLine(pts);
  const n = line.length / 2;
  if (n < 2) return { positions, uvs, indices };

  const half = width / 2;
  let along = 0;

  for (let i = 0; i < n; i++) {
    const x = line[i * 2];
    const z = line[i * 2 + 1];
    if (i > 0) {
      along += Math.hypot(x - line[(i - 1) * 2], z - line[(i - 1) * 2 + 1]);
    }

    // Incoming / outgoing unit directions.
    let inX = 0;
    let inZ = 0;
    let outX = 0;
    let outZ = 0;
    if (i > 0) {
      const dx = x - line[(i - 1) * 2];
      const dz = z - line[(i - 1) * 2 + 1];
      const l = Math.max(EPS, Math.hypot(dx, dz));
      inX = dx / l;
      inZ = dz / l;
    }
    if (i < n - 1) {
      const dx = line[(i + 1) * 2] - x;
      const dz = line[(i + 1) * 2 + 1] - z;
      const l = Math.max(EPS, Math.hypot(dx, dz));
      outX = dx / l;
      outZ = dz / l;
    }
    if (i === 0) {
      inX = outX;
      inZ = outZ;
    }
    if (i === n - 1) {
      outX = inX;
      outZ = inZ;
    }

    // Perpendicular of the averaged tangent, scaled so the offset edge stays parallel.
    let tx = inX + outX;
    let tz = inZ + outZ;
    let tl = Math.hypot(tx, tz);
    if (tl < 1e-6) {
      // Hairpin: fall back to the incoming normal.
      tx = inX;
      tz = inZ;
      tl = Math.max(EPS, Math.hypot(tx, tz));
    }
    tx /= tl;
    tz /= tl;
    const mx = -tz;
    const mz = tx;
    const nOutX = -outZ;
    const nOutZ = outX;
    const denom = mx * nOutX + mz * nOutZ;
    let scale = Math.abs(denom) < 1e-3 ? MITER_LIMIT : 1 / denom;
    if (scale > MITER_LIMIT) scale = MITER_LIMIT;
    if (scale < -MITER_LIMIT) scale = -MITER_LIMIT;
    const ox = mx * half * scale;
    const oz = mz * half * scale;

    positions.push(x - ox, y, z - oz, x + ox, y, z + oz);
    const v = along / Math.max(1, width);
    uvs.push(0, v, 1, v);

    if (i > 0) {
      // left/right of the previous vertex, then of this one. Wound so the face normal is
      // +Y — Three.js treats counter-clockwise-as-seen-from-the-normal as the front face,
      // and we want roads visible from above with backface culling on.
      const a = (i - 1) * 2;
      const b = a + 1;
      const c = i * 2;
      const d = c + 1;
      indices.push(a, b, c, b, d, c);
    }
  }

  return { positions, uvs, indices };
}

/** Removes consecutive duplicate vertices from an open polyline. */
export function dedupeLine(pts: Poly, tolerance = 1e-3): Poly {
  const out: Poly = [];
  for (let i = 0; i < pts.length; i += 2) {
    const m = out.length / 2;
    if (m > 0) {
      const dx = out[(m - 1) * 2] - pts[i];
      const dz = out[(m - 1) * 2 + 1] - pts[i + 1];
      if (Math.abs(dx) < tolerance && Math.abs(dz) < tolerance) continue;
    }
    out.push(pts[i], pts[i + 1]);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Headings
// ---------------------------------------------------------------------------

/**
 * Three.js convention: an object's local -Z is forward, so a `rotation.y` of `h` points
 * it at `(-sin h, 0, -cos h)`. With +Z as south, heading 0 is due north.
 */
export function headingFromDirection(dx: number, dz: number): number {
  return Math.atan2(-dx, -dz);
}

export function directionFromHeading(h: number): { x: number; z: number } {
  return { x: -Math.sin(h), z: -Math.cos(h) };
}

/**
 * A compass bearing — degrees clockwise from north, which is how a direction is read off a
 * north-up map — as a unit vector in world space.
 *
 * Deliberately not the same number as a heading. `headingFromDirection` is the Three.js
 * rotation the car mesh gets, and it runs anticlockwise; a host dragging an arrow on the
 * region picker is thinking "north-east", not "minus 45 degrees of yaw". The conversion
 * lives here so that only one place has to know both conventions.
 */
export function directionFromBearing(degrees: number): { dx: number; dz: number } {
  const r = (degrees * Math.PI) / 180;
  return { dx: Math.sin(r), dz: -Math.cos(r) };
}

/** Signed shortest angular difference, in (-PI, PI]. */
export function angleDelta(from: number, to: number): number {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d <= -Math.PI) d += Math.PI * 2;
  return d;
}

// ---------------------------------------------------------------------------
// Clipping to the region rectangle
// ---------------------------------------------------------------------------

export interface Rect {
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
}

export function rectIntersects(rect: Rect, b: Rect): boolean {
  return !(b.maxX < rect.minX || b.minX > rect.maxX || b.maxZ < rect.minZ || b.minZ > rect.maxZ);
}

/**
 * Sutherland–Hodgman against an axis-aligned rectangle.
 *
 * Overpass hands back whole ways, so a single `natural=water` relation for the Vistula
 * can stretch tens of kilometres past a 1.5 km race region. Clipping keeps the vertex
 * count — and the float range Three.js sees — proportional to the region the host chose.
 */
export function clipPolygonToRect(poly: Poly, rect: Rect): Poly {
  let out = poly;
  const edges: [(x: number, z: number) => boolean, (ax: number, az: number, bx: number, bz: number) => [number, number]][] = [
    [(x) => x >= rect.minX, (ax, az, bx, bz) => lerpToX(ax, az, bx, bz, rect.minX)],
    [(x) => x <= rect.maxX, (ax, az, bx, bz) => lerpToX(ax, az, bx, bz, rect.maxX)],
    [(_x, z) => z >= rect.minZ, (ax, az, bx, bz) => lerpToZ(ax, az, bx, bz, rect.minZ)],
    [(_x, z) => z <= rect.maxZ, (ax, az, bx, bz) => lerpToZ(ax, az, bx, bz, rect.maxZ)],
  ];
  for (const [inside, intersect] of edges) {
    if (out.length === 0) return [];
    const next: Poly = [];
    const n = out.length / 2;
    for (let i = 0; i < n; i++) {
      const j = (i + n - 1) % n;
      const cx = out[i * 2];
      const cz = out[i * 2 + 1];
      const px = out[j * 2];
      const pz = out[j * 2 + 1];
      const cIn = inside(cx, cz);
      const pIn = inside(px, pz);
      if (cIn) {
        if (!pIn) next.push(...intersect(px, pz, cx, cz));
        next.push(cx, cz);
      } else if (pIn) {
        next.push(...intersect(px, pz, cx, cz));
      }
    }
    out = next;
  }
  return cleanRing(out, 1e-3);
}

function lerpToX(ax: number, az: number, bx: number, bz: number, x: number): [number, number] {
  const t = Math.abs(bx - ax) < EPS ? 0 : (x - ax) / (bx - ax);
  return [x, az + (bz - az) * t];
}

function lerpToZ(ax: number, az: number, bx: number, bz: number, z: number): [number, number] {
  const t = Math.abs(bz - az) < EPS ? 0 : (z - az) / (bz - az);
  return [ax + (bx - ax) * t, z];
}

export interface ClippedLine {
  pts: Poly;
  /** Parallel to `pts`; -1 marks a vertex synthesised on the clip boundary. */
  ids: number[];
}

/** Liang–Barsky per segment, stitching consecutive in-rect pieces into runs. */
export function clipPolylineToRect(pts: Poly, ids: number[], rect: Rect): ClippedLine[] {
  const runs: ClippedLine[] = [];
  let current: ClippedLine | null = null;
  const n = pts.length / 2;

  for (let i = 1; i < n; i++) {
    const ax = pts[(i - 1) * 2];
    const az = pts[(i - 1) * 2 + 1];
    const bx = pts[i * 2];
    const bz = pts[i * 2 + 1];
    const clipped = clipSegment(ax, az, bx, bz, rect);
    if (!clipped) {
      current = null;
      continue;
    }
    const { t0, t1 } = clipped;
    const p0x = ax + (bx - ax) * t0;
    const p0z = az + (bz - az) * t0;
    const p1x = ax + (bx - ax) * t1;
    const p1z = az + (bz - az) * t1;
    const id0 = t0 <= EPS ? (ids[i - 1] ?? -1) : -1;
    const id1 = t1 >= 1 - EPS ? (ids[i] ?? -1) : -1;

    if (!current) {
      current = { pts: [p0x, p0z], ids: [id0] };
      runs.push(current);
    }
    current.pts.push(p1x, p1z);
    current.ids.push(id1);
    // The segment left the rectangle before its own end: the run stops here.
    if (t1 < 1 - EPS) current = null;
  }

  return runs.filter((r) => r.pts.length >= 4);
}

function clipSegment(
  ax: number,
  az: number,
  bx: number,
  bz: number,
  rect: Rect,
): { t0: number; t1: number } | null {
  let t0 = 0;
  let t1 = 1;
  const dx = bx - ax;
  const dz = bz - az;
  const tests: [number, number][] = [
    [-dx, ax - rect.minX],
    [dx, rect.maxX - ax],
    [-dz, az - rect.minZ],
    [dz, rect.maxZ - az],
  ];
  for (const [p, q] of tests) {
    if (Math.abs(p) < EPS) {
      if (q < 0) return null;
      continue;
    }
    const r = q / p;
    if (p < 0) {
      if (r > t1) return null;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return null;
      if (r < t1) t1 = r;
    }
  }
  return t1 > t0 ? { t0, t1 } : null;
}

// ---------------------------------------------------------------------------
// Multipolygon ring assembly
// ---------------------------------------------------------------------------

/**
 * Stitches the member ways of an OSM multipolygon into closed rings.
 *
 * OSM hands you an unordered bag of open way fragments; a ring is complete when walking
 * from fragment endpoint to fragment endpoint gets you back where you started. Fragments
 * that never close (common where a relation straddles the bbox edge) are discarded.
 */
export function assembleRings(fragments: Poly[], tolerance = 0.5): Poly[] {
  const pool = fragments.filter((f) => f.length >= 4).map((f) => f.slice());
  const rings: Poly[] = [];
  const near = (ax: number, az: number, bx: number, bz: number) =>
    Math.abs(ax - bx) <= tolerance && Math.abs(az - bz) <= tolerance;

  while (pool.length) {
    let ring = pool.pop()!;
    let extended = true;
    while (extended) {
      extended = false;
      const rEndX = ring[ring.length - 2];
      const rEndZ = ring[ring.length - 1];
      if (near(ring[0], ring[1], rEndX, rEndZ) && ring.length >= 8) break;
      for (let i = 0; i < pool.length; i++) {
        const frag = pool[i];
        const fStartX = frag[0];
        const fStartZ = frag[1];
        const fEndX = frag[frag.length - 2];
        const fEndZ = frag[frag.length - 1];
        if (near(rEndX, rEndZ, fStartX, fStartZ)) {
          ring = ring.concat(frag.slice(2));
          pool.splice(i, 1);
          extended = true;
          break;
        }
        if (near(rEndX, rEndZ, fEndX, fEndZ)) {
          const reversed: Poly = [];
          for (let k = frag.length - 2; k >= 0; k -= 2) reversed.push(frag[k], frag[k + 1]);
          ring = ring.concat(reversed.slice(2));
          pool.splice(i, 1);
          extended = true;
          break;
        }
      }
    }
    const closed =
      ring.length >= 8 &&
      near(ring[0], ring[1], ring[ring.length - 2], ring[ring.length - 1]);
    if (closed) rings.push(cleanRing(ring));
  }
  return rings;
}
